<template>
  <div class="row">
    <div class="col-12">
      <h5 data-cy="application-form-section-contact-person">
        {{ $t('message.generic.contactPerson') }}
      </h5>
    </div>
    <!-- contactPerson -->
    <!-- Gender -->
    <div class="col-12">
      <label class="d-block">{{ $t('message.generic.title.title') }} {{ sending ? '*' : '' }}</label>
      <coozzy-form-radio
        v-model="company.contactPerson.gender"
        name="gender"
        class="mr-3"
        data-cy="application-form-company-salutation-mr-radio"
        select-value="MALE"
        :state="$v.gender.$dirty && $v.gender.$error ? false : null">
        {{ $t('message.generic.title.mr') }}
      </coozzy-form-radio>
      <coozzy-form-radio
        v-model="company.contactPerson.gender"
        name="gender"
        class="mr-3"
        data-cy="application-form-company-salutation-ms-radio"
        select-value="FEMALE">
        {{ $t('message.generic.title.mrs') }}
      </coozzy-form-radio>
      <coozzy-form-radio
        v-model="company.contactPerson.gender"
        name="gender"
        class="mr-3"
        data-cy="application-form-company-salutation-divers-radio"
        select-value="OTHER">
        {{ $t('message.generic.title.divers') }}
      </coozzy-form-radio>
    </div>
    <!-- Name -->
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="company.contactPerson.firstName"
        data-cy="application-form-company-first-name-input"
        :name="$t('message.generic.firstName') + (sending ? '*' : '' )"
        :state="$v.firstName.$dirty && $v.firstName.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="company.contactPerson.lastName"
        data-cy="application-form-company-last-name-input"
        :name="$t('message.generic.lastName') + (sending ? '*' : '' )"
        :state="$v.lastName.$dirty && $v.lastName.$error ? false : null"
        type="text" />
    </div>
    <div class="col-sm-12 col-md-6 mb-3">
      <coozzy-form-input
        v-model="company.contactPerson.position"
        data-cy="application-form-company-occupation-input"
        :name="$t('message.generic.jobTitle')+ (sending ? '*' : '' )"
        :state="$v.position.$dirty && $v.position.$error ? false : null" />
    </div>
    <!-- Contact info (phone, email) -->
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="company.contactPerson.email"
        data-cy="application-form-company-email-input"
        :name="$t('message.generic.email') + (sending ? '*' : '' )"
        :state="$v.email.$dirty && $v.email.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="company.contactPerson.phoneNumber"
        data-cy="application-form-company-phone-input"
        :name="$t('message.generic.phone.home') + (sending ? '*' : '' )"
        :state="$v.phoneNumber.$dirty && $v.phoneNumber.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="company.contactPerson.mobileNumber"
        data-cy="application-form-company-mobile-input"
        :name="$tc('message.generic.phone.mobile')"
        type="text" />
    </div>
    <div class="col-sm-12 col-md-6">
      <coozzy-form-input
        v-model="company.contactPerson.fax"
        data-cy="application-form-company-fax-input"
        type="tel"
        :name="$tc('message.generic.phone.fax')" />
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import { mapMutations } from 'vuex'
import { validation, isEmailValid } from '@/mixins/validation'
import { required } from 'vuelidate/lib/validators'
import * as Sentry from '@sentry/vue'
import { ApplicationFormCompany, ApplicationFormCompanyContactPersonDateState } from '@/types/models'

export default Vue.extend({
  name: 'ApplicationFormCompanyContactPerson',
  components: { CoozzyFormInput, CoozzyFormRadio },
  mixins: [validation],
  props: {
    company: {
      type: Object as PropType<ApplicationFormCompany>,
      default: null
    },
    sending: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dates: {
        dob: null
      }
    }
  },
  computed: {
    gender: {
      get(): string {
        return this.company.contactPerson.gender
      },
      set(value: string) {
        const com = this.company
        com.contactPerson.gender = value
        this.updateCompany(com)
      }
    },
    firstName: {
      get(): string {
        return this.company.contactPerson.firstName
      },
      set(value: string) {
        const com = this.company
        com.contactPerson.firstName = value
        this.updateCompany(com)
      }
    },
    lastName: {
      get(): string {
        return this.company.contactPerson.lastName
      },
      set(value: string) {
        const com = this.company
        com.contactPerson.lastName = value
        this.updateCompany(com)
      }
    },
    email: {
      get(): string {
        return this.company.contactPerson.email
      },
      set(value: string) {
        const com = this.company
        com.contactPerson.email = value
        this.updateCompany(com)
      }
    },
    phoneNumber: {
      get(): string {
        return this.company.contactPerson.phoneNumber
      },
      set(value: string) {
        const com = this.company
        com.contactPerson.phoneNumber = value
        this.updateCompany(com)
      }
    },
    mobileNumber: {
      get(): string {
        return this.company.contactPerson.mobileNumber
      },
      set(value: string) {
        const com = this.company
        com.contactPerson.mobileNumber = value
        this.updateCompany(com)
      }
    },
    fax: {
      get(): string {
        return this.company.contactPerson.fax
      },
      set(value: string) {
        const com = this.company
        com.contactPerson.fax = value
        this.updateCompany(com)
      }
    },
    position: {
      get(): string {
        return this.company.contactPerson.position
      },
      set(value: string) {
        const com = this.company
        com.contactPerson.position = value
        this.updateCompany(com)
      }
    }
  },
  mounted() {
    const dateState = this.dates as ApplicationFormCompanyContactPersonDateState
    const timestampToDateInput = this.$options.filters?.timestampToDateInput as
      | ((value: string | null) => string | null)
      | undefined
    dateState.dob = timestampToDateInput ? timestampToDateInput(dateState.dob) : dateState.dob
  },
  methods: {
    ...mapMutations('applicationForm', ['updateCompany']),
    syncContactPersonDobToUnixSeconds() {
      const dateState = this.dates as ApplicationFormCompanyContactPersonDateState
      dateState.dob = this.dateInputToUnixSecondsString(dateState.dob)
    },
    dateInputToUnixSecondsString(strDate: string | null): string | null {
      if (!strDate) {
        return null
      }
      return Math.floor(Date.parse(strDate) / 1000).toString()
    },
    isInvalid() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        Sentry.setExtra('companyContactPersonValidation', this.$v)
        this.scrollToError()
        return true
      }
      return false
    },
    isInvalidEmail() {
      this.$v.email.$touch()
      if (this.$v.email.$invalid) {
        Sentry.setExtra('companyContactPersonEmailValidation', this.$v)
        this.scrollToError()
        return true
      }
      return false
    }
  },
  validations: {
    gender: {
      required
    },
    firstName: {
      required
    },
    lastName: {
      required
    },
    email: {
      isEmailValid,
      required
    },
    phoneNumber: {
      required
    },
    position: {
      required
    }
  }
})
</script>

<style lang="scss" scoped>
.custom-radio {
  display: inline-block;
}
</style>
